// Amigos e chat: conexão em tempo real (SSE) e a gaveta social que fica em
// todas as telas fora da partida (botão no canto inferior direito).
import { api, errorMessage, type ChatMessage, type FriendsResponse } from "./api";
import { config } from "./config";
import { feature } from "./features";
import { t } from "./i18n";
import { currentUser, onSessionChange } from "./session";
import { loadSprites, spriteHtml } from "./sprites";
import { esc } from "./ui";
import { isBlocked, reportButton } from "./report";

type Tab = "global" | "friends" | "dm";

interface StreamEvent { type: string; message?: ChatMessage; with?: string; to?: string; request?: { nickname: string } }

let source: EventSource | null = null;
let root: HTMLElement | null = null;
let open = false;
let tab: Tab = "global";
let dmWith: { id: string; nickname: string } | null = null;
let globalMsgs: ChatMessage[] = [];
let dmMsgs: ChatMessage[] = [];
let friends: FriendsResponse = { friends: [], incoming: [], outgoing: [] };
let unread = 0;
const unreadDm = new Map<string, number>();
let hidden = false;

/** Liga/desliga a gaveta conforme a sessão e a tela (na partida ela some). */
export function installSocial() {
  onSessionChange(() => sync());
  window.addEventListener("hashchange", () => sync());
  window.addEventListener("pokechess:blocks", () => render());
  sync();
}

function sync() {
  const logged = !!currentUser();
  const inGame = /^#\/(partida)/.test(location.hash);
  hidden = !logged || inGame;
  if (logged) connect();
  else disconnect();
  render();
}

function connect() {
  if (source) return;
  source = new EventSource(`${config.apiUrl}/chat/stream`, { withCredentials: true });
  source.onmessage = (e) => {
    let ev: StreamEvent;
    try {
      ev = JSON.parse(e.data);
    } catch {
      return;
    }
    if (ev.type === "global" && ev.message) {
      globalMsgs = [...globalMsgs.slice(-99), ev.message];
      if (!(open && tab === "global") && ev.message.from.id !== currentUser()?.id) unread++;
    } else if (ev.type === "dm" && ev.message) {
      const other = ev.message.from.id === currentUser()?.id ? ev.to! : ev.message.from.id;
      if (dmWith?.id === other) dmMsgs = [...dmMsgs, ev.message];
      if (!(open && tab === "dm" && dmWith?.id === other) && ev.message.from.id !== currentUser()?.id) {
        unreadDm.set(other, (unreadDm.get(other) ?? 0) + 1);
        unread++;
      }
    } else if (ev.type === "friends") {
      if (ev.request) unread++;
      void loadFriends();
    }
    render();
  };
}

function disconnect() {
  source?.close();
  source = null;
  globalMsgs = [];
  dmMsgs = [];
  unread = 0;
  unreadDm.clear();
  open = false;
}

async function loadFriends() {
  try {
    friends = await api.friends();
  } catch {
    /* offline */
  }
  render();
}

async function openTab(next: Tab) {
  tab = next;
  if (tab === "global" && feature("globalChat")) {
    try {
      globalMsgs = (await api.globalChat()).messages;
    } catch {
      /* ok */
    }
  }
  if (tab === "friends") await loadFriends();
  render();
  scrollToEnd();
}

async function openDm(id: string, nickname: string) {
  dmWith = { id, nickname };
  tab = "dm";
  unread = Math.max(0, unread - (unreadDm.get(id) ?? 0));
  unreadDm.delete(id);
  try {
    dmMsgs = (await api.dm(id)).messages;
  } catch {
    dmMsgs = [];
  }
  render();
  scrollToEnd();
}

function scrollToEnd() {
  const list = root?.querySelector<HTMLElement>("[data-msgs]");
  if (list) list.scrollTop = list.scrollHeight;
}

function msgHtml(m: ChatMessage): string {
  const mine = m.from.id === currentUser()?.id;
  if (!mine && isBlocked(m.from.id)) return "";
  const time = new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `<li class="msg ${mine ? "mine" : ""}">${spriteHtml(m.from.avatar ?? 25, 28)}<div><b>${esc(m.from.nickname)}</b>${mine ? "" : reportButton(m.from.id, m.from.nickname)} <span class="bw-dim small">${time}</span><br>${esc(m.text)}</div></li>`;
}

function render() {
  if (!root) {
    root = document.createElement("div");
    root.className = "social";
    document.body.appendChild(root);
    root.addEventListener("click", onClick);
    root.addEventListener("submit", onSubmit);
    void loadSprites().then(render);
  }
  root.hidden = hidden;
  if (hidden) return;
  const badge = unread + friends.incoming.length;
  const tabs: Tab[] = feature("globalChat") ? ["global", "friends"] : ["friends"];
  if (!feature("globalChat") && tab === "global") tab = "friends";
  let body = "";
  if (tab === "global") {
    body = `<ul class="msgs" data-msgs>${globalMsgs.map(msgHtml).join("") || `<li class="bw-dim small">${t("social.empty")}</li>`}</ul>
      <form class="chat-form" data-send="global"><input class="bw-input" name="text" maxlength="200" autocomplete="off" placeholder="${t("social.say")}"><button class="bw-btn bw-btn--blue">${t("social.send")}</button></form>`;
  } else if (tab === "friends") {
    body = `<form class="chat-form" data-add><input class="bw-input" name="username" maxlength="40" autocomplete="off" placeholder="${t("social.addPlaceholder")}"><button class="bw-btn bw-btn--green">${t("social.add")}</button></form>
      <div data-social-alert hidden></div>
      ${friends.incoming.length ? `<p class="social-h">${t("social.incoming")}</p><ul class="friends">${friends.incoming.map((f) => `<li>${spriteHtml(f.avatar ?? 25, 28)}<span>${esc(f.nickname)} <span class="bw-dim small">@${esc(f.username)}</span></span>
        <button class="bw-btn bw-btn--green" data-accept="${f.id}">✔</button><button class="bw-btn" data-remove="${f.id}">✕</button></li>`).join("")}</ul>` : ""}
      <p class="social-h">${t("social.friends", { n: friends.friends.length })}</p>
      <ul class="friends">${friends.friends.map((f) => `<li>${spriteHtml(f.avatar ?? 25, 28)}<span><i class="dot ${f.online ? "on" : ""}"></i>${esc(f.nickname)} <span class="bw-dim small">@${esc(f.username)}</span></span>
        <button class="bw-btn bw-btn--blue" data-dm="${f.id}" data-name="${esc(f.nickname)}">💬${unreadDm.get(f.id) ? ` ${unreadDm.get(f.id)}` : ""}</button><button class="bw-btn" data-remove="${f.id}" title="${t("social.remove")}">✕</button>${reportButton(f.id, f.nickname)}</li>`).join("") || `<li class="bw-dim small">${t("social.noFriends")}</li>`}</ul>
      ${friends.outgoing.length ? `<p class="social-h">${t("social.outgoing")}</p><ul class="friends">${friends.outgoing.map((f) => `<li>${spriteHtml(f.avatar ?? 25, 28)}<span>${esc(f.nickname)}</span><button class="bw-btn" data-remove="${f.id}">✕</button></li>`).join("")}</ul>` : ""}`;
  } else if (tab === "dm" && dmWith) {
    body = `<p class="social-h"><button class="bw-btn" data-back>◀</button> ${esc(dmWith.nickname)}</p>
      <ul class="msgs" data-msgs>${dmMsgs.map(msgHtml).join("") || `<li class="bw-dim small">${t("social.empty")}</li>`}</ul>
      <form class="chat-form" data-send="dm"><input class="bw-input" name="text" maxlength="200" autocomplete="off" placeholder="${t("social.say")}"><button class="bw-btn bw-btn--blue">${t("social.send")}</button></form>`;
  }
  const typed = root.querySelector<HTMLInputElement>("input[name=text]")?.value ?? "";
  root.innerHTML = `
    <button class="bw-btn bw-btn--blue social-toggle" data-toggle aria-expanded="${open}" aria-label="${t("social.title")}">💬${badge ? `<span class="social-badge">${badge}</span>` : ""}</button>
    ${open ? `<section class="bw-panel social-panel" role="dialog" aria-label="${t("social.title")}">
      <div class="bw-tabs">${tabs.map((k) => `<button class="bw-btn ${tab === k || (k === "friends" && tab === "dm") ? "bw-btn--blue" : ""}" data-tab="${k}">${t(k === "global" ? "social.global" : "social.friendsTab")}${k === "friends" && friends.incoming.length ? ` (${friends.incoming.length})` : ""}</button>`).join("")}
        <button class="bw-btn bw-btn--square" data-toggle aria-label="${t("common.close")}">✕</button></div>
      ${body}
    </section>` : ""}`;
  const input = root.querySelector<HTMLInputElement>("input[name=text]");
  if (input && typed) input.value = typed;
  scrollToEnd();
}

function showAlert(msg: string, kind = "error") {
  const a = root?.querySelector<HTMLElement>("[data-social-alert]");
  if (!a) return;
  a.className = `bw-alert bw-alert--${kind}`;
  a.textContent = msg;
  a.hidden = false;
}

async function onClick(e: Event) {
  const el = (e.target as HTMLElement).closest<HTMLElement>("button");
  if (!el) return;
  try {
    if (el.hasAttribute("data-toggle")) {
      open = !open;
      if (open) {
        if (tab === "global") unread = 0;
        await openTab(tab === "dm" ? "friends" : tab);
      } else render();
    } else if (el.dataset.tab) {
      if (el.dataset.tab === "global") unread = 0;
      await openTab(el.dataset.tab as Tab);
    } else if (el.dataset.accept) {
      await api.acceptFriend(el.dataset.accept);
      await loadFriends();
    } else if (el.dataset.remove) {
      await api.removeFriend(el.dataset.remove);
      await loadFriends();
    } else if (el.dataset.dm) {
      await openDm(el.dataset.dm, el.dataset.name ?? "");
    } else if (el.hasAttribute("data-back")) {
      dmWith = null;
      await openTab("friends");
    }
  } catch (err) {
    showAlert(errorMessage(err));
  }
}

async function onSubmit(e: Event) {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  try {
    if (form.hasAttribute("data-add")) {
      const name = String(new FormData(form).get("username") ?? "").trim();
      if (!name) return;
      const r = await api.friendRequest(name);
      await loadFriends();
      showAlert(r.status === "accepted" ? t("social.nowFriends") : t("social.requestSent"), "ok");
      return;
    }
    const input = form.querySelector<HTMLInputElement>("input[name=text]")!;
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    // a mensagem volta pelo stream (para todos, inclusive quem enviou)
    if (form.dataset.send === "global") await api.postGlobal(text);
    else if (dmWith) await api.postDm(dmWith.id, text);
  } catch (err) {
    const list = root?.querySelector<HTMLElement>("[data-msgs]");
    if (list) list.insertAdjacentHTML("beforeend", `<li class="bw-alert bw-alert--error">${esc(errorMessage(err))}</li>`);
    else showAlert(errorMessage(err));
  }
}
